<template>
  <div>
    <b-button class="mr-1" variant="primary" size="sm" @click="openModal">
      <i class="fa-solid fa-coins"></i>
      {{ $t('monetary.addTransaction') }}
    </b-button>
    <b-button v-if="hasMultipleAccounts" class="mr-1" variant="dark" size="sm" @click="openBetweenAccountModal">
      <i class="fa-solid fa-exchange"></i>
      {{ $t('monetary.moveMoneyBetweenAccounts') }}
    </b-button>
    <!-- Money summary: deposits (bonus excluded) / withdrawals / bonus — centered over the table -->
    <div class="cmt-totals">
      <div class="cmt-stat cmt-stat--deposit">
        <span class="cmt-stat__icon"><i class="fa-solid fa-arrow-down"></i></span>
        <span class="cmt-stat__body">
          <span class="cmt-stat__label">{{ $t('monetary.totalDeposits') }}</span>
          <span class="cmt-stat__value">{{ convertToEURO(totalDepositSum) }}</span>
        </span>
      </div>
      <div class="cmt-stat cmt-stat--withdraw">
        <span class="cmt-stat__icon"><i class="fa-solid fa-arrow-up"></i></span>
        <span class="cmt-stat__body">
          <span class="cmt-stat__label">{{ $t('monetary.totalWithdrawals') }}</span>
          <span class="cmt-stat__value">{{ convertToEURO(totalWithdrawSum) }}</span>
        </span>
      </div>
      <div class="cmt-stat cmt-stat--bonus">
        <span class="cmt-stat__icon"><i class="fa-solid fa-gift"></i></span>
        <span class="cmt-stat__body">
          <span class="cmt-stat__label">{{ $t('monetary.totalBonus') }}</span>
          <span class="cmt-stat__value">{{ convertToEURO(totalBonusSum) }}</span>
        </span>
      </div>
    </div>

    <div class="cmt-toolbar">
      <!-- Segmented switch: All / Deposits / Withdrawals -->
      <div class="cmt-switch">
        <button type="button" class="cmt-switch__opt" :class="{ 'is-active': type === null }"
                @click="setType(null)">
          <i class="fa-solid fa-layer-group"></i>{{ $t('monetary.allTransactions') }}
        </button>
        <button type="button" class="cmt-switch__opt cmt-switch__opt--deposit" :class="{ 'is-active': type === 'DEPOSIT' }"
                @click="setType('DEPOSIT')">
          <i class="fa-solid fa-arrow-down"></i>{{ $t('monetary.deposits') }}
        </button>
        <button type="button" class="cmt-switch__opt cmt-switch__opt--withdraw" :class="{ 'is-active': type === 'WITHDRAWAL' }"
                @click="setType('WITHDRAWAL')">
          <i class="fa-solid fa-arrow-up"></i>{{ $t('monetary.withdrawals') }}
        </button>
      </div>

      <!-- Bonus rows are hidden by default; this pill reveals them -->
      <button type="button" class="cmt-bonus" :class="{ 'is-on': showBonus }"
              v-b-tooltip.hover :title="$t('monetary.showBonus')"
              @click="showBonus = !showBonus">
        <i class="fa-solid fa-gift"></i>{{ $t('monetary.showBonus') }}
        <span class="cmt-bonus__dot" :class="{ 'is-on': showBonus }"></span>
      </button>
    </div>
    <div v-bind:key="refreshKey">
      <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>
      <b-table responsive
               v-handy-scroll
               v-bind:items="monetaries"
               v-bind:fields="fields"
               no-local-sorting
               v-bind:sort-by.sync="sort.field"
               v-bind:sort-desc.sync="sortIsDesc"
               striped
               hover
               @sort-changed="onSortChanged"
               style="white-space: nowrap; padding-bottom: 30px"
      >
        <template #head(selection)="data">
          <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" :checked="selected.all"
                           style="margin-top: 7px"
                           @change="val=>toggleAllRow(val)" inline
          ></b-form-checkbox>
        </template>


        <template #cell(registrationTime)="data">
          {{ data.value| momentDateTime }}
          <!-- Creation date moved manually → mark it, tooltip shows the original -->
          <i v-if="data.item.originalRegistrationTime" class="fa-solid fa-clock-rotate-left text-warning ml-25"
             v-b-tooltip.hover :title="`${$t('monetary.dateChanged')} — ${$t('monetary.originalDate')}: ${$options.filters.momentDateTime(data.item.originalRegistrationTime)}`"></i>
        </template>
        <template #cell(realType)="data">
        <span style="font-size:12px" :style="{ color: data.value === 'REAL' ? '#28c76f' : '#ea5455' }"
        >{{
            data.value.toLowerCase()
                .charAt(0)
                .toUpperCase() + data.value.toLowerCase()
                .slice(1)
          }}</span>
        </template>
        <template v-if="!isClientTable" #cell(amount)="data">
          <div class="d-flex justify-content-center align-items-center text-center">
            <span :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">{{ convertToEURO(data.value, data.item.currency) }}</span>
          </div>
        </template>
        <template v-else #cell(amount)="data">
          <p class="p-0 m-0" :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">
            <span v-if="data.item.originalCurrency != data.item.currency">{{ convertToEURO(data.item.originalAmount, data.item.originalCurrency) }} ({{ convertToEURO(data.value, data.item.currency) }})</span>
            <span v-else>{{ convertToEURO(data.value, data.item.currency) }}</span>
          </p>

        </template>
        <template #cell(currency)="data">
          <p class="p-0 m-0">
            <span v-if="data.item.originalCurrency != data.item.currency">{{data.item.originalCurrency}} → </span>
            <span>{{data.value}}</span>
          </p>

        </template>
        <template #cell(action)="data">
          {{$t(`monetaryActions.${data.value}.name`)}}
        </template>
        <template #cell(type)="data">
          <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
        </template>
        <!-- Client comment column (visible to the client in the webtrader) -->
        <template #cell(clientComment)="data">
          <span v-if="data.value" v-b-tooltip.hover.bottom="data.value"
                class="d-inline-block text-truncate align-middle" style="max-width: 180px;"
          >{{ data.value }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(id)="data">
          <!-- The id itself stays hidden — the button copies it to the clipboard -->
          <b-button size="sm" variant="flat-secondary" class="btn-icon cmt-copy-id"
                    v-b-tooltip.hover :title="$t('operations.copyId')"
                    @click.stop="copyId(data.item.id)">
            <i class="fa-regular fa-copy"></i>
          </b-button>
        </template>
        <template #cell(depositorUserId)="data">
          <span v-if="data.value">{{ getUserFullName(data.value) }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(statusUserId)="data">
          <span v-if="data.value">{{ getUserFullName(data.value) }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(flags)="data">
          <!-- FTD_STD_CHANGES holders get a dropdown to reclassify the row;
               everyone else sees the read-only badges. TXF (action TAXES) rows
               can NEVER be FTD/STD — no dropdown for them (backend rejects it
               anyway). -->
          <b-dropdown v-if="hasPermission(permissions.FTD_STD_CHANGES) && data.item.action !== 'TAXES'"
                      size="sm" variant="flat-secondary" no-caret boundary="viewport"
                      toggle-class="p-25"
                      v-b-tooltip.hover :title="$t('monetary.ftdStatus.change')">
            <template #button-content>
              <b-badge v-if="data.item.isFtd" variant="light-success" class="mr-25">FTD</b-badge>
              <b-badge v-if="data.item.isStd" variant="light-info" class="mr-25">STD</b-badge>
              <b-badge v-if="data.item.ftdType" variant="light-warning" class="mr-25">{{ ftdTypeLabel(data.item.ftdType) }}</b-badge>
              <span v-if="!data.item.isFtd && !data.item.isStd && !data.item.ftdType" class="text-muted">—</span>
              <i class="fa-solid fa-caret-down text-muted ml-25"></i>
            </template>
            <b-dropdown-item v-for="option in ftdStatusOptions" :key="option"
                             :active="isCurrentFtdStatus(data.item, option)"
                             @click="changeFtdStatus(data.item, option)">
              {{ option === 'NONE' ? $t('monetary.ftdStatus.none') : ftdTypeLabel(option) }}
            </b-dropdown-item>
          </b-dropdown>
          <template v-else>
            <b-badge v-if="data.item.isFtd" variant="light-success" class="mr-25">FTD</b-badge>
            <b-badge v-if="data.item.isStd" variant="light-info" class="mr-25">STD</b-badge>
            <b-badge v-if="data.item.ftdType" variant="light-warning">{{ ftdTypeLabel(data.item.ftdType) }}</b-badge>
            <span v-if="!data.item.isFtd && !data.item.isStd && !data.item.ftdType" class="text-muted">—</span>
          </template>
        </template>

        <template #cell(actions)="data">

          <div class="btn-group equal-buttons">
            <b-button class="btn btn-info" size="sm" @click="openCommentModal(data.item)"
                      v-b-tooltip.hover title="Comment">
              <i class="fa fa-sticky-note"></i>
            </b-button>
            <!-- Manual creation-date edit (MONETARY_UPDATE_DATE; backend enforces too) -->
            <b-button v-if="hasPermission(permissions.MONETARY_UPDATE_DATE)"
                      class="btn btn-warning" size="sm" @click="$refs.dateModal.open(data.item)"
                      v-b-tooltip.hover :title="$t('monetary.editDate')">
              <i class="fa-solid fa-calendar-days"></i>
            </b-button>

            <!-- Status decisions: MONETARY_APPROVE holders only (backend enforces too) -->
            <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED' && hasPermission(permissions.MONETARY_APPROVE)"
                      class="btn btn-success" size="sm"
                      @click="handleMonetaryEvent(data.item.id,'approve')"
                      v-b-tooltip.hover title="Approve">
              <i class="fa fa-check"></i>
            </b-button>

            <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED' && hasPermission(permissions.MONETARY_APPROVE)"
                      class="btn btn-warning" size="sm"
                      @click="handleMonetaryEvent(data.item.id,'fake')"
                      v-b-tooltip.hover title="Fake Approve">
              <i class="fa fa-check-circle"></i>
            </b-button>

            <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED' && hasPermission(permissions.MONETARY_APPROVE)"
                      class="btn btn-danger" size="sm"
                      @click="handleMonetaryEvent(data.item.id,'decline')"
                      v-b-tooltip.hover title="Decline">
              <i class="fa fa-times"></i>
            </b-button>

            <!-- Approved but never reached the trading system (e.g. old "Fake Approve") -->
            <b-button v-if="needsCoreResend(data.item) && hasPermission(permissions.MONETARY_APPROVE)"
                      variant="outline-danger" size="sm" @click="resendToCoreConfirm(data.item)"
                      v-b-tooltip.hover :title="$t('monetary.resendCore.action')">
              <i class="fa-solid fa-rotate"></i>
            </b-button>

            <b-button v-if="data.item.realType === 'FAKE' && hasPermission(permissions.MONETARY_UPDATE)"
                      class="btn btn-success" size="sm"
                      @click="confirmConvertToReal(data.item)"
                      v-b-tooltip.hover :title="$t('monetary.convertToReal.action')">
              <i class="fa-solid fa-wand-magic-sparkles"></i>
            </b-button>
            <span v-if="canShowConvertToFake(data.item)" class="d-inline-block"
                  v-b-tooltip.hover :title="fakeBlockedReason(data.item) || $t('monetary.convertToFake.action')">
              <b-button class="btn btn-warning" size="sm"
                        :disabled="!!fakeBlockedReason(data.item)"
                        @click="confirmConvertToFake(data.item)">
                <i class="fa-solid fa-ban"></i>
              </b-button>
            </span>

            <b-button
                v-if="hasPermission(permissions.MONETARY_DELETE)"

                class="btn btn-danger"
                size="sm"
                style="margin-left: 1px"
                @click="confirmDelete(data.item)"
                v-b-tooltip.hover
                title="Delete"
            >
              <i class="fa fa-trash"></i>
            </b-button>

          </div>

        </template>

        <template #cell(paymentDetails.creditCardNumber)="data">
          <template v-if="data.item.paymentDetails!=null && data.item.paymentDetails.creditCardNumber!=null">
              <span class="mr-1">Card: {{ data.value }}</span>
              <span class="mr-1">Exp: {{ data.item.paymentDetails.creditCardExp }}</span>
              <span class="">CVV: {{ data.item.paymentDetails.creditCardCvv }}</span>
          </template>
        </template>
        <template #cell(paymentDetails)="data">
          <span>{{ data.value['processorName'] }}</span>
        </template>
        <template #cell(selection)="data">
          <b-form-checkbox v-model="selectedIds[data.item.id]" inline @input="val=>toggleRow(data.item, val)"
          ></b-form-checkbox>
        </template>


      </b-table>


      <!--{{pagination}}-->
      <div class="mt-2 text-center">
        <div class="d-inline-block">
          <b-pagination
              v-model="pagination.page"
              v-bind:total-rows="pagination.total"
              v-bind:per-page="pagination.size"
              @page-click="onPageClick"
              align="center"
          ></b-pagination>
        </div>
        <div class="d-inline-block">
          <b-select class="d-inline-block" style="width: 70px" size="sm" v-model="pagination.size" @change="refresh">
  
            <b-select-option v-bind:value="25">25</b-select-option>
            <b-select-option v-bind:value="50">50</b-select-option>
            <b-select-option v-bind:value="100">100</b-select-option>
          </b-select>
          /{{ pagination.total }} {{ $t('columns.counterNames.monetaries') }}

        </div>

      </div>
    </div>
    <monetary-account-modal ref="monetary-account-modal" :affiliate="affiliate" :client="client"
                            @transaction-added="$emit('transaction-added')"
    />
    <monetary-multi-account-modal ref="monetary-multipleAccount-modal"/>
    <monetary-date-modal ref="dateModal" @saved="refresh"/>
    <b-modal v-model="isCommentModalVisible" :title="title" @hide="closeCommentModal" ok-variant="success"
             cancel-variant="danger"
             @ok="saveComment" @cancel="closeCommentModal" ok-title="Save" cancel-title="Cancel"
             :cancel-title="$t('operations.cancel')" :ok-title="$t('operations.save')"
    >
      <!-- Internal note: CRM eyes only -->
      <div class="cmt-section cmt-section--internal">
        <label class="cmt-section__label">
          <i class="fa-solid fa-user-shield mr-50"></i>{{ $t('monetary.commentModal.internalTitle') }}
        </label>
        <small class="cmt-section__hint">{{ $t('monetary.commentModal.internalHint') }}</small>
        <textarea v-model="commentInternal" class="form-control" rows="3"></textarea>
      </div>

      <!-- Client comment: shown in the webtrader -->
      <div class="cmt-section cmt-section--client">
        <label class="cmt-section__label">
          <i class="fa-solid fa-eye mr-50"></i>{{ $t('monetary.commentModal.clientTitle') }}
        </label>
        <small class="cmt-section__hint">{{ $t('monetary.commentModal.clientHint') }}</small>
        <textarea v-model="commentClient" class="form-control" rows="3"></textarea>
      </div>
    </b-modal>
    <b-modal
        id="delete-confirmation-modal"
        v-model="showDeleteModal"
        title="Confirm Delete"
        centered
        ok-title="Delete"
        ok-variant="danger"
        @ok="deleteConfirmed"
    >

      <div v-if="monetaryToDelete">

        <p class="text-danger font-weight-bold mb-1">
          Are you sure you want to delete this monetary entry?
        </p>

        <ul class="list-unstyled mb-2">
          <li><strong>ID:</strong> {{ monetaryToDelete.id }}</li>
          <li><strong>Status:</strong> {{ monetaryToDelete.status }}</li>
          <li><strong>Amount:</strong> {{ monetaryToDelete.amount }}</li>
          <li><strong>Client:</strong> {{ monetaryToDelete.clientId }}</li>
          <li><strong>Type:</strong> {{ monetaryToDelete.type }}</li>
        </ul>

        <p class="text-warning">This action cannot be undone.</p>

      </div>

    </b-modal>

    <!-- Convert FAKE ⇄ REAL confirmation (direction = convertTarget) -->
    <b-modal
        id="convert-to-real-confirmation-modal"
        v-model="showConvertToRealModal"
        :title="$t(`${convertKey}.title`)"
        centered
        :ok-title="$t(`${convertKey}.confirm`)"
        :ok-variant="convertTarget === 'FAKE' ? 'warning' : 'success'"
        :cancel-title="$t('operations.cancel')"
        @ok="convertToRealConfirmed"
    >

      <div v-if="monetaryToConvert">

        <p class="font-weight-bold mb-1">
          {{ $t(`${convertKey}.question`, { id: monetaryToConvert.id }) }}
        </p>

        <ul class="list-unstyled mb-2">
          <li><strong>ID:</strong> {{ monetaryToConvert.id }}</li>
          <li><strong>{{ $t('columns.amount') }}:</strong> {{ convertToEURO(monetaryToConvert.amount, monetaryToConvert.currency) }}</li>
          <li><strong>{{ $t('columns.type') }}:</strong> {{ monetaryToConvert.type }}</li>
        </ul>

        <p class="text-warning mb-0">{{ $t(`${convertKey}.warning`) }}</p>

      </div>

    </b-modal>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import ClientCell from '@/components/partial/clients/ClientCell';
import UserSelectBox from '@/components/widget/select/UserSelectBox';
import MonetariesSearchRequest from '@/model/monetary/MonetarySearchRequest';
import MonetaryAccountModal from '@/components/modal/monetaryAccountModal.vue';
import MonetaryDateModal from '@/components/modal/MonetaryDateModal.vue';
import MonetaryMultiAccountModal from '@/components/modal/monetaryMultiAccountModal';
import UserPicker from '@/components/widget/picker/userPicker.vue';
import mixinPermissions from '@/components/mixin/mixinPermissions';

// "Bonus" rows are transactions whose monetary action is a credit/bonus grant —
// not real client money. They are hidden by default and excluded from the
// deposit/withdraw totals; the "Show bonus" switch reveals them in the table.
const BONUS_ACTIONS = ['CREDIT', 'BONUS'];
const NON_BONUS_ACTIONS = ['CREDIT_LINE', 'CHARGE_BACK', 'DEBIT', 'TAXES', 'PAYMENT', 'AFFILIATE_PAYMENT'];
// Manual FTD/STD statuses (FTD_STD_CHANGES permission). FTD_A..FTD_E are
// stored on the transaction's ftdType without setting isFtd — dashboards merge
// their amount into the client's FTD without counting an extra FTD.
const FTD_STATUS_OPTIONS = ['NONE', 'FTD', 'STD', 'FTD_A', 'FTD_B', 'FTD_C', 'FTD_D', 'FTD_E'];

export default {
  name: 'ClientMonetaryTable',
  components: {
    UserPicker,
    MonetaryAccountModal,
    MonetaryDateModal,
    MonetaryMultiAccountModal,
    ClientCell,
    UserSelectBox,
  },
  mixins: [mixinBase, mixinTable, mixinNotifications, mixinPermissions],
  data: () => ({
    showDeleteModal: false,
    monetaryToDelete: null,
    showConvertToRealModal: false,
    convertTarget: 'REAL', // 'REAL' | 'FAKE' — direction of the convert modal
    monetaryToConvert: null,
    monetaries: [],
    affiliate: {},
    clients: {},
    selectedItem: {}, // Store the selected item
    title: '',
    commentInternal: '',
    commentClient: '',
    isCommentModalVisible: false,
    type: null,
    showBonus: false,
    totalDepositSum: 0,
    totalWithdrawSum: 0,
    totalBonusSum: 0,
    accounts: [],

    // Status whitelist is set ONCE here so the deep `filters` watcher never
    // sees a status reassignment from refresh() (which would loop).
    // DECLINED is excluded — those records aren't real transactions and
    // shouldn't show in the client's transaction history.
    // Bonus rows (action CREDIT/BONUS) are filtered out by default.
    filters: {
      ...MonetariesSearchRequest(),
      status: ['REQUEST', 'PENDING', 'APPROVED', 'CANCELED'],
      action: [...NON_BONUS_ACTIONS],
    },
    selected: { all: false },
    headerColor: '',
    selectedOption: null,
    showDetails: false,
    ftdStatusOptions: FTD_STATUS_OPTIONS,

  }),
  props: {
    isClientTable: {
      default: () => false
    },
    clientId: {
      type: String,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    columns: {
      default: () => ([]),
      required: true
    },
    hasSelection: {
      default: () => false
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    },
    showBonus(val) {
      // null action = no filter → bonus rows included.
      this.filters.action = val ? null : [...NON_BONUS_ACTIONS]; // -> deep watcher -> refresh()
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    this.filters['clientDetails']['clientId'] = this.clientId;
    if (!this.lazy) {
      this.refresh();
    }
  },
  computed: {
    // i18n block of the convert modal for the current direction
    convertKey() {
      return this.convertTarget === 'FAKE' ? 'monetary.convertToFake' : 'monetary.convertToReal';
    },
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById', 'getUserFullName']),
    hasMultipleAccounts() {
      return Array.isArray(this.accounts) && this.accounts.length > 1;
    },
    fields() {
      let $this = this;
      // Virtual/nested columns the backend can't sort on (+ the icon-only
      // copy-id column, where a sort arrow would look broken).
      const unsortable = ['flags', 'depositorUserId', 'statusUserId', 'id'];
      let fields = (this.columns || []).map(column => {
        return {
          key: column.key,
          label: $this.$t(`columns.${column.label || column.key}`),
          sortable: !unsortable.includes(column.key),
          active: column.enable,
        };
      })
          .sort((a, b) => {
            if (a.key === 'id') return -1; // Move 'type' field to the second position
            if (b.key === 'id') return 1;
            return 0;
          });
      fields.push({
        key: 'paymentDetails.processorName',
        label: 'PSP',
        class: 'text-center',
        sortable: true,
        active: true,
      });
      fields.push({
        key: 'paymentDetails.creditCardNumber',
        label: 'Credit Card',
        class: 'text-center',
        sortable: true,
        active: true,
      });

      fields.push({
        key: 'paymentDetails.comments',
        label: 'Comment',
        class: 'text-center',
        sortable: false,
        active: true,
      });
      fields.push({
        key: 'clientComment',
        label: $this.$t('columns.clientComment'),
        class: 'text-center',
        sortable: false,
        active: true,
      });
      fields.push({
        key: 'actions',
        label: 'Actions',
        class: 'text-center',
        sortable: false,
        active: true,
      });
      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }
      return fields.filter(f => f.active === true);
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
  },
  methods: {
    ...mapActions('monetary', ['getAllMonetary', 'updateMonetary', 'handleEvent','delete', 'updateMonetaryRealStatus', 'updateMonetaryFtdStatus', 'resendToCore']),
    ...mapActions('account', ['getAccountsByClient']),
    ...mapActions('affiliate', ['getAffiliateByClientId']),

    confirmDelete(item) {
      this.monetaryToDelete = item;   // store full record
      this.showDeleteModal = true;    // open modal
    },

    deleteConfirmed() {
      this.deleteMonetary(this.monetaryToDelete.id, 'delete');
      this.showDeleteModal = false;
    }
,
    // ---- Convert FAKE ⇄ REAL (with confirmation) ----
    confirmConvertToReal(item) {
      this.monetaryToConvert = item;   // store full record
      this.convertTarget = 'REAL';
      this.showConvertToRealModal = true; // open modal
    },
    confirmConvertToFake(item) {
      if (this.fakeBlockedReason(item)) return;
      this.monetaryToConvert = item;
      this.convertTarget = 'FAKE';
      this.showConvertToRealModal = true;
    },
    // ---- Convert REAL → FAKE (MONETARY_CONVERT_TO_FAKE) ----
    // Same rules as the backend (updateMonetaryRealType): only REAL, never a
    // TXF; FTD/STD rows show the button disabled with the reason.
    canShowConvertToFake(item) {
      return !!item && item.realType === 'REAL' && item.action !== 'TAXES'
          && this.hasPermission(this.permissions.MONETARY_CONVERT_TO_FAKE);
    },
    fakeBlockedReason(item) {
      if (item && (item.isFtd || item.isStd || item.ftdType)) return this.$t('monetary.convertToFake.blockedFtdStd');
      return null;
    },
    async convertToRealConfirmed() {
      const $this = this;
      const item = this.monetaryToConvert;
      this.showConvertToRealModal = false;
      if (!item) return;

      await this.updateMonetaryRealStatus({ id: item.id, realType: this.convertTarget })
          .then((updatedMonetary) => {
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              // Update the properties of the found Monetary object
              Object.assign(monetaryToUpdate, updatedMonetary);
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
              $this.$emit('input', $this.monetaries);
            }
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
          })
          .finally(() => {
            $this.monetaryToConvert = null;
          });
    },
    // "FTD_A" → "FTD A" for the badge / dropdown labels.
    ftdTypeLabel(value) {
      return value ? value.replace('_', ' ') : '';
    },
    isCurrentFtdStatus(item, option) {
      if (option === 'FTD') return !!item.isFtd;
      if (option === 'STD') return !!item.isStd;
      if (option === 'NONE') return !item.isFtd && !item.isStd && !item.ftdType;
      return item.ftdType === option;
    },
    async changeFtdStatus(item, ftdStatus) {
      const $this = this;
      if (this.isCurrentFtdStatus(item, ftdStatus)) return;
      await this.updateMonetaryFtdStatus({ id: item.id, ftdStatus })
          .then((updatedMonetary) => {
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              // $set — ftdType may not exist on the row yet, so a plain
              // Object.assign would not be reactive.
              $this.$set(monetaryToUpdate, 'isFtd', updatedMonetary.isFtd);
              $this.$set(monetaryToUpdate, 'isStd', updatedMonetary.isStd);
              $this.$set(monetaryToUpdate, 'ftdType', updatedMonetary.ftdType);
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
              $this.$emit('input', $this.monetaries);
            }
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
          });
    },
    // Same rule as MonetariesTable: approved + trading account + never sent to the trading system
    needsCoreResend(item) {
      if (!item || item.status !== 'APPROVED') return false
      if (!item.clientDetails || !item.clientDetails.accountId) return false
      if (item.action === 'TAXES') return false
      if (['ACCOUNT_TRANSFER', 'CORE_ONLY', 'SYNC_CORE_ONLY'].includes(item.realType)) return false
      return !item.extTransactionId && item.hasBeenProcessed !== true
    },
    async resendToCoreConfirm(item) {
      const amount = item.originalAmount != null
          ? `${item.originalAmount} ${item.originalCurrency || ''}`
          : this.convertToEURO(item.amount || 0)
      const ok = await this.$bvModal.msgBoxConfirm(this.$t('monetary.resendCore.confirmText', {
        id: item.id, amount, account: item.clientDetails.accountId,
      }), {
        title: this.$t('monetary.resendCore.confirmTitle'),
        okTitle: this.$t('monetary.resendCore.action'),
        cancelTitle: this.$t('operations.cancel'),
        okVariant: 'danger',
        centered: true,
      }).catch(() => false)
      if (!ok) return
      this.resendToCore({ id: item.id })
          .then(() => {
            this.showSuccessNotification(this.$t('monetary.resendCore.done', { id: item.id }))
            this.refresh()
          })
          .catch(err => this.showErrorNotification(this.$t('monetary.resendCore.failed'), this.getErrorDescription(err)))
    },
    async handleMonetaryEvent(id, event) {
      const $this = this;

      await this.handleEvent({
        id,
        event
      })
          .then((updatedMonetary) => {
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              // Update the properties of the found Monetary object
              Object.assign(monetaryToUpdate, updatedMonetary);
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
              $this.$emit('input', $this.monetaries);
            }
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
    },
    async deleteMonetary(id) {
      const $this = this;

      await this.delete({ id })
          .then((deletedMonetary) => {

            const index = $this.monetaries.findIndex(m => m.id === deletedMonetary.id);

            if (index !== -1) {
              $this.monetaries.splice(index, 1);
              $this.showSuccessNotification(
                  $this.$t('globalMessages.deleteDataSuccess', { system: $this.$t('menu.monetaries') })
              );

              $this.$emit('input', $this.monetaries);
              $this.$emit('transaction-added');
            }
          })
          .catch(err => {
            $this.showErrorNotification(
                $this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }),
                err.response.data.error
            );
          });
    }
,
    openCommentModal(item) {
      this.selectedItem = {
        monetary: item,
        client: this.clients[item.clientDetails.clientId]
      }; // Store the selected item
      this.title = this.getTitle();
      // Internal note lives on `comment`; older rows only have the legacy
      // paymentDetails.comments — prefill from it so nothing seems lost.
      this.commentInternal = item.comment || (item.paymentDetails && item.paymentDetails.comments) || '';
      this.commentClient = item.clientComment || '';
      this.isCommentModalVisible = true;
    },
    closeCommentModal () {
      this.isCommentModalVisible = false;
      this.selectedItem = {}; // Clear the selected item
      this.commentInternal = '';
      this.commentClient = '';
    },
    getTitle() {
      let monetary = { ...this.selectedItem.monetary };
      let client = { ...this.selectedItem.client };
      let title = '';
      if (monetary.clientDetails != null) {
        let accountId = monetary.clientDetails.accountId || '';
        title = accountId + ' ' + client.firstName + ' ' + client.lastName;
      }
      return 'Comment Transaction ' + title;

    },
    async saveComment() {
      let $this = this;
      if (this.selectedItem.monetary.paymentDetails == null) {
        this.selectedItem.monetary.paymentDetails = {};
      }

      // Internal note → transaction.comment (canonical) + legacy
      // paymentDetails.comments kept in sync so the old column still shows it.
      this.selectedItem.monetary.comment = this.commentInternal;
      this.selectedItem.monetary.paymentDetails.comments = this.commentInternal;
      // Client comment → shown to the client in the webtrader.
      this.selectedItem.monetary.clientComment = this.commentClient;
      await this.updateMonetary({
        monetary: this.selectedItem.monetary,
        id: this.selectedItem.monetary.id
      })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
      this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
      this.$emit('input', this.monetary);
      this.isCommentModalVisible = false;
      this.selectedItem = {}; // Clear the selected item
      this.commentInternal = '';
      this.commentClient = '';

    },
    copyId(id) {
      if (!id) return;
      const done = () => this.showSuccessNotification(this.$t('globalMessages.copySuccess'));
      if (this.$copyText) {
        this.$copyText(String(id)).then(done, () => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(String(id)).then(done, () => {});
      }
    },
    openModal() {
      this.$refs['monetary-account-modal'].showModal(this.clientId);
    },
    openBetweenAccountModal() {
      this.$refs['monetary-multipleAccount-modal'].showModal(this.clientId);
    },

    /**
     * Single entry point for the All / Deposits / Withdrawals switch.
     * Sets the active type, then assigns it onto `filters` — the deep `filters`
     * watcher performs exactly ONE refresh. (Previously each button set `type`
     * AND called refresh(), while refresh() also re-assigned `filters.type`,
     * which re-triggered the watcher and caused a double fetch.)
     */
    setType(type) {
      if (this.type === type) return;
      this.type = type;
      this.resetResponse();
      this.pagination.page = 1;
      this.filters.type = type; // -> deep watcher -> refresh()
    },
    resetResponse() {
      this.monetaries = [];
      this.clients = {};
    },
    /**
     * Whole-history money summary for this client: deposits and withdrawals
     * WITHOUT bonus rows, plus the bonus total on its own. Independent of the
     * type switch / bonus toggle. Uses the backend's whole-set `sum` when the
     * server provides it, otherwise sums the returned rows (one client's
     * transactions comfortably fit a single page).
     */
    refreshTotals() {
      const base = {
        clientDetails: { clientId: this.clientId },
        status: ['REQUEST', 'PENDING', 'APPROVED', 'CANCELED'],
      };
      const sumOf = extra => this.getAllMonetary({
        filter: { ...base, ...extra },
        pagination: { page: 1, size: 1000 },
        sort: { field: 'lastUpdate', direction: 'DESC' },
      }).then(result => {
        const data = (result && result.data) || {};
        if (data['sum'] != null) {
          return Number(data['sum']) || 0;
        }
        // Older backend without the whole-set `sum` field — sum the rows.
        return (data['monetaryTransactions'] || [])
            .reduce((t, m) => t + (Number(m.amount) || 0), 0);
      }).catch(err => {
        console.warn('client monetary totals query failed', err);
        return 0;
      });

      Promise.all([
        sumOf({ type: 'DEPOSIT', action: [...NON_BONUS_ACTIONS] }),
        sumOf({ type: 'WITHDRAWAL', action: [...NON_BONUS_ACTIONS] }),
        sumOf({ action: [...BONUS_ACTIONS] }),
      ]).then(([deposits, withdrawals, bonus]) => {
        this.totalDepositSum = deposits;
        this.totalWithdrawSum = withdrawals;
        this.totalBonusSum = bonus;
      });
    },

    getStatusVariant(status) {
      switch (status) {
        case 'DEPOSIT':
          return 'success';
        case 'CARD':
          return 'warning';
        case 'WITHDRAWAL':
          return 'danger';
        default:
          return 'primary';
      }
    },
    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }

      this.filters['clientDetails']['clientId'] = this.clientId;
      const result = await this.getAllMonetary({
        pagination: this.pagination,
        sort,
        filter: this.filters,
      })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      // Old-lead/duplicate check context — a failure (no permission, no
      // affiliate) must NEVER toast or break the tab: without it the modal
      // just keeps the strict default gating.
      this.getAffiliateByClientId(this.clientId)
          .then(affiliate => {
            $this.affiliate = affiliate;
          })
          .catch(() => {});
      this.monetaries = result.data['monetaryTransactions'];
      this.clients = result.data['clients'].reduce((map, obj) => (map[obj.id] = obj, map), {});
      this.refreshTotals();
      this.accounts = await this.getAccountsByClient(this.clientId);
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
    },
    reGenerateAccounts(accounts) {
      return accounts.map(account => {
        const label = account.isDemo ? `${account.id} Demo-` : `${account.id} Real-`;
        const displayLabel = `${label} ${account.balance} ${account.currency}`;
        return {
          ...account,
          displayLabel
        }; // Spread account fields and add displayLabel
      });
    },
  },

};
</script>

<style scoped>
.equal-buttons .btn {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 34px !important;   /* same height for all buttons */
  min-height: 34px !important;
  padding: 0 10px !important; /* keeps nice spacing */
}

/* ── Money summary tiles — centered over the table ───────────────────── */
.cmt-totals {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin: 1rem 0 0.85rem;
}
.cmt-stat {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 200px;
  padding: 0.55rem 1.1rem 0.55rem 0.6rem;
  border-radius: 14px;
  color: #fff;
  box-shadow: 0 10px 22px -12px rgba(20, 24, 50, 0.5);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.cmt-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -12px rgba(20, 24, 50, 0.55);
}
.cmt-stat__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.95rem;
  flex-shrink: 0;
}
.cmt-stat__body {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.cmt-stat__label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
  white-space: nowrap;
}
.cmt-stat__value {
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
}
.cmt-stat--deposit { background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%); }
.cmt-stat--withdraw { background: linear-gradient(135deg, #fb7185 0%, #ef4444 60%, #c1272d 100%); }
.cmt-stat--bonus { background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #5b21b6 100%); }

/* ── Transaction-type toolbar + segmented switch ─────────────────────── */
.cmt-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin: 0 0 1rem;
}

/* "Show bonus" pill — same visual family as the segmented switch */
.cmt-bonus {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid rgba(124, 58, 237, 0.28);
  background: rgba(124, 58, 237, 0.06);
  color: #7c3aed;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.45rem 0.95rem;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}
.cmt-bonus:hover {
  background: rgba(124, 58, 237, 0.12);
}
.cmt-bonus.is-on {
  background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #5b21b6 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(124, 58, 237, 0.6);
}
.cmt-bonus__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(124, 58, 237, 0.35);
  transition: background 0.18s ease;
}
.cmt-bonus__dot.is-on {
  background: #4ade80;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.9);
}

.cmt-switch {
  display: inline-flex;
  background: #f0f1f7;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
}
.cmt-switch__opt {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.45rem 0.95rem;
  border-radius: 9px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}
.cmt-switch__opt:hover { color: #2b2f42; }
.cmt-switch__opt.is-active {
  background: #fff;
  color: #007bff;
  box-shadow: 0 3px 9px rgba(20, 24, 50, 0.13);
}
.cmt-switch__opt--deposit.is-active { color: #28c76f; }
.cmt-switch__opt--withdraw.is-active { color: #ea5455; }

/* Dark mode */
body.dark-layout .cmt-switch { background: var(--crm-dark-surface-2, #2e3850); }
body.dark-layout .cmt-switch__opt { color: #b4b7bd; }
body.dark-layout .cmt-switch__opt:hover { color: #fff; }
body.dark-layout .cmt-switch__opt.is-active { background: var(--crm-dark-surface, #232c3e); }
body.dark-layout .cmt-bonus {
  background: rgba(167, 139, 250, 0.1);
  border-color: rgba(167, 139, 250, 0.3);
  color: #c4b5fd;
}
body.dark-layout .cmt-bonus.is-on {
  background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #5b21b6 100%);
  color: #fff;
}

@media (max-width: 575px) {
  .cmt-switch { width: 100%; }
  .cmt-switch__opt { flex: 1; justify-content: center; padding: 0.45rem 0.5rem; }
}

/* Two-section comment modal: internal (CRM only) vs client-visible */
.cmt-section {
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  border-left-width: 3px;
}
.cmt-section + .cmt-section { margin-top: 0.9rem; }
.cmt-section__label {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.1rem;
}
.cmt-section__hint {
  display: block;
  color: #94a3b8;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}
.cmt-section--internal { border-left-color: #7367f0; background: rgba(115, 103, 240, 0.05); }
.cmt-section--internal .cmt-section__label { color: #5e50ee; }
.cmt-section--client { border-left-color: #007BFF; background: rgba(0, 123, 255, 0.05); }
.cmt-section--client .cmt-section__label { color: #007BFF; }
.dark-layout .cmt-section { border-color: rgba(255,255,255,0.12); }
</style>
